import { useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Starts every newly opened page at the top. RootLayout stays mounted across routes, so the
 * window otherwise keeps whatever offset the previous page was scrolled to.
 *
 * Two kinds of navigation are left alone:
 *
 * - **Back / forward** (`POP`): the browser restores where that page was left, which is what
 *   those buttons promise.
 * - **A link with a hash** (`/#openingHours`): `SectionLink` scrolls to the section itself,
 *   and smoothly; jumping to the top first would only make it start over from there.
 *
 * Not react-router's `<ScrollRestoration>`, which would cover the first case but also handles
 * hashes itself — with an instant `scrollIntoView` that cuts the smooth scroll short, and
 * aims at the wrong place besides (see `scrollToSection`).
 *
 * Keyed on the location, not the path, so a link to the page already open ("Főoldal" while
 * reading the opening hours further down) still takes the reader back to the top.
 */
const ScrollToTopOnNavigate = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Layout effect: the new page is in the DOM but not yet painted, so it never flashes up at
  // the old offset first.
  useLayoutEffect(() => {
    if (navigationType === "POP" || location.hash) return;
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return null;
};

export default ScrollToTopOnNavigate;
